import '../../assets/css/case-property.css';
import '../styles/property-hero-responsive.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useVirtualTours, useSlotGroup, EditableSlotGroup, useEditMode } from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath } from './propertyUtils';
import { openTourScheduler } from './TourSchedulerModal';

export const FURNISHED_CTA_DEF: SlotGroupDefinition = {
  label: 'Furnished Homes CTA',
  description: 'Only shown on the live site when a URL is set. Also adds a menu link under Floor Plans on every page of the property.',
  fields: {
    cta_text: { label: 'CTA Text', type: 'text', defaultValue: 'Furnished Homes' },
    cta_url: { label: 'CTA URL', type: 'url', defaultValue: '' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_blank' },
  },
};

/**
 * Property interior page hero.
 * Full-width cover image with headline text and CTA buttons.
 * Matches the Stratman wp-block-cover pattern.
 */
export function PropertyHero() {
  const property = useProperty();
  const virtualTours = useVirtualTours();

  const basePath = getPropertyBasePath(property);
  const image = useContentToken('pagehero.background_image') || property.property_featured_image || '';
  const titleLine1 = useContentToken('pagehero.title_line1');
  const titleLine2 = useContentToken('pagehero.title_line2');
  const floorplansCta = useContentToken('pagehero.floorplans_cta');
  const tourCta = useContentToken('pagehero.tour_cta');
  const furnishedCta = useSlotGroup('pagehero.furnished_cta', FURNISHED_CTA_DEF);
  const isEditMode = useEditMode()?.isEditMode ?? false;
  const furnishedUrl = furnishedCta.cta_url || '';
  const furnishedTarget = furnishedCta._target || '_blank';
  const city = property.property_address_city || '';
  const state = property.property_address_state || '';
  const virtualTourUrl = virtualTours.length > 0
    ? (virtualTours[0] as any).virtual_tour_url
    : ((property as any).property_virtual_tour_url || (property as any).property_3d_tour_url || '');
  const virtualTourGalleryUrl = `${basePath}/photos?section=virtual-tours#virtual-tours`;

  return (
    <div className="prop-hero">
      <div className="prop-hero-bg">
        <EditableToken path="pagehero.background_image" type="image" label="Background Image" as="div" style={{ width: '100%', height: '100%' }} defaultField={contentSchema.tokens['pagehero.background_image'].defaultField}>
          {image && (
            <VibesitesImage
              url={image}
              alt={`${city}${state ? `, ${state}` : ''}`}
              width={1456}
              height={473}
              priority
            />
          )}
        </EditableToken>
      </div>
      <div className="prop-hero-content">
        <EditableToken path="pagehero.title_line1" type="text" label="Title Line 1" as="h2" className="prop-hero-title-1" defaultField={contentSchema.tokens['pagehero.title_line1'].defaultField} defaultValue={contentSchema.tokens['pagehero.title_line1'].defaultValue}>
          {titleLine1}
        </EditableToken>
        <EditableToken path="pagehero.title_line2" type="text" label="Title Line 2" as="h2" className="prop-hero-title-2" defaultField={contentSchema.tokens['pagehero.title_line2'].defaultField} defaultValue={contentSchema.tokens['pagehero.title_line2'].defaultValue}>
          {titleLine2}
        </EditableToken>
        <div className="prop-hero-ctas">
          <EditableToken path="pagehero.floorplans_cta" type="text" label="Floor Plans CTA" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['pagehero.floorplans_cta'].defaultValue}>
            <a className="prop-btn" href={`${basePath}/floor-plans/`}>{floorplansCta || 'View Floor Plans'}</a>
          </EditableToken>
          {(furnishedUrl || isEditMode) && (
            <EditableSlotGroup groupPath="pagehero.furnished_cta" definition={FURNISHED_CTA_DEF} style={{ display: 'contents' }}>
              <a
                className="prop-btn"
                href={furnishedUrl || undefined}
                target={furnishedTarget}
                rel={furnishedTarget === '_blank' ? 'noopener noreferrer' : undefined}
                style={furnishedUrl ? undefined : { opacity: 0.5 }}
              >
                {furnishedCta.cta_text || 'Furnished Homes'}
              </a>
            </EditableSlotGroup>
          )}
          <EditableToken path="pagehero.tour_cta" type="text" label="Tour CTA" as="span" style={{ display: 'contents' }} defaultValue={contentSchema.tokens['pagehero.tour_cta'].defaultValue}>
            <button type="button" className="prop-btn" onClick={openTourScheduler} data-vibesite-event="schedule_tour_clicked">{tourCta || 'Schedule a Tour'}</button>
          </EditableToken>
          {virtualTourUrl && (
            <a className="prop-btn" href={virtualTourGalleryUrl} data-vibesite-event="virtual_tour_clicked">View 3D Virtual Tour</a>
          )}
        </div>
      </div>
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Property Hero',
  tokens: {
    'pagehero.title_line1': {
      label: 'Title Line 1',
      type: 'text',
      defaultField: 'property_home_hero_title',
      defaultValue: 'Comfortable',
    },
    'pagehero.title_line2': {
      label: 'Title Line 2',
      type: 'text',
      defaultField: 'property_home_romance_title',
      defaultValue: 'homes, great community',
    },
    'pagehero.background_image': {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    'pagehero.floorplans_cta': {
      label: 'Floor Plans CTA Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    'pagehero.tour_cta': {
      label: 'Tour CTA Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
  },
  slotGroups: {
    'pagehero.furnished_cta': FURNISHED_CTA_DEF,
  },
};

(PropertyHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyHero;
